/* Editor Dashboard — assigned queue view. */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, RefreshCw, Clock3, CheckCircle2, XCircle, AlertTriangle,
  ChevronRight, AlarmClock,
} from 'lucide-react';
import BulkAiRunBar from './BulkAiRunBar';

const API  = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const http = axios.create({ baseURL: API, withCredentials: true });

const TONE = {
  pending:            { label: 'Pending',           icon: Clock3,        cls: 'text-amber-700 bg-amber-500/15'      },
  assigned:           { label: 'Assigned',          icon: Clock3,        cls: 'text-sky-700 bg-sky-500/15'          },
  approved:           { label: 'Approved',          icon: CheckCircle2,  cls: 'text-emerald-700 bg-emerald-500/15'  },
  changes_requested:  { label: 'Changes requested', icon: AlertTriangle, cls: 'text-orange-700 bg-orange-500/15'    },
  rejected:           { label: 'Rejected',          icon: XCircle,       cls: 'text-rose-700 bg-rose-500/15'        },
};

function hoursSince(iso) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return (Date.now() - t) / 3600_000;
}

export default function EditorDashboard() {
  const [rows,     setRows]     = useState([]);
  const [status,   setStatus]   = useState('open');
  const [loading,  setLoading]  = useState(false);
  const [selected, setSelected] = useState([]);
  const navigate = useNavigate();

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await http.get(`/editor/reviews/mine?status=${status}`);
      setRows(r.data?.rows || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load queue.');
    } finally { setLoading(false); }
  }, [status]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => { setSelected([]); }, [status]);

  const toggle = (id) => setSelected((cur) =>
    cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);

  return (
    <section data-testid="editor-dashboard" className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[16px] font-semibold">My review queue</div>
          <div className="text-[11.5px] text-muted-foreground">
            Rows assigned to you. Pro-writer admins see the full queue.
          </div>
        </div>
        <div className="flex items-center gap-1">
          {['open', 'closed', ''].map((s) => (
            <button key={s || 'all'} onClick={() => setStatus(s)}
                    data-testid={`ed-filter-${s || 'all'}`}
                    className={`rounded-full border px-2 py-0.5 text-[10.5px] uppercase tracking-wider ${
                      status === s
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-border bg-background hover:bg-secondary'}`}>
              {s || 'all'}
            </button>
          ))}
          <button onClick={refresh}
                  data-testid="ed-refresh-btn"
                  className="ml-2 inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-[11px] hover:bg-secondary">
            {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
            Refresh
          </button>
        </div>
      </div>

      <BulkAiRunBar
        selectedIds={selected}
        onClear={() => setSelected([])}
        onDone={() => { setSelected([]); refresh(); }}
      />

      {rows.length === 0 ? (
        <div className="rounded-md border border-dashed border-border bg-card p-8 text-center text-[12px] text-muted-foreground">
          Nothing in this bucket.
        </div>
      ) : (
        <ul className="divide-y divide-border rounded-md border border-border bg-card">
          {rows.map((r) => {
            const t = TONE[r.status] || { label: r.status };
            const Icon = t.icon || Clock3;
            const hrs = hoursSince(r.submitted_at);
            const overdue = r.escalated === true;
            return (
              <li key={r.id} data-testid={`ed-row-${r.id}`}
                  className="flex cursor-pointer items-center gap-3 px-4 py-3 hover:bg-secondary/60">
                <input type="checkbox" checked={selected.includes(r.id)}
                       onClick={(e) => e.stopPropagation()}
                       onChange={() => toggle(r.id)}
                       data-testid={`ed-row-${r.id}-checkbox`}
                       className="h-3.5 w-3.5 shrink-0" />
                <span onClick={() => navigate(`/editor/manuscript/${r.id}`)}
                      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wider ${t.cls}`}>
                  <Icon className="h-3 w-3" />
                  {t.label}
                </span>
                <div className="flex-1" onClick={() => navigate(`/editor/manuscript/${r.id}`)}>
                  <div className="text-[13px] font-medium">{r.book_title || r.book_id}</div>
                  <div className="text-[10.5px] text-muted-foreground">
                    submitted by {r.submitted_by} · {(r.submitted_at || '').slice(0, 10)} · {r.sample_chapter_count} sample ch.
                  </div>
                </div>
                {hrs != null && (r.status === 'pending' || r.status === 'assigned') && (
                  <span onClick={() => navigate(`/editor/manuscript/${r.id}`)}
                        className={`inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] uppercase tracking-wider ${
                    overdue
                      ? 'bg-rose-500/15 text-rose-700 border-rose-500/40'
                      : hrs > 16
                        ? 'bg-amber-500/15 text-amber-800 border-amber-500/40'
                        : 'bg-sky-500/15 text-sky-800 border-sky-500/40'
                  }`}>
                    <AlarmClock className="h-2.5 w-2.5" />
                    {hrs < 1 ? `${Math.round(hrs * 60)}m` : `${hrs.toFixed(1)}h`}
                    {overdue && ' · Overdue'}
                  </span>
                )}
                <ChevronRight className="h-4 w-4 text-muted-foreground" onClick={() => navigate(`/editor/manuscript/${r.id}`)} />
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
